import { windowWidth } from './constants';


// Heads-up display overlay: the level (top-left) and a segmented shield bar
// (top-right), drawn directly over the game (no panel). Lives in its own
// always-on scene so it renders on top; hidden on the splash/credits screens.
export class UIScene extends Phaser.Scene {
    private static readonly MAX_SHIELDS = 10;
    private static readonly SEG_W = 11;
    private static readonly SEG_GAP = 3;
    private static readonly SEG_H = 14;
    private static readonly ROW_Y = 16;                  // top of the shield bar
    private static readonly BAR_RIGHT = windowWidth - 16; // right edge of the bar

    private hud!: Phaser.GameObjects.Container;
    private levelText!: Phaser.GameObjects.Text;
    private shieldBar!: Phaser.GameObjects.Graphics;
    private dangerTween: Phaser.Tweens.Tween | null = null;

    constructor() {
        super({ key: 'UIScene', active: true });
    }

    create() {
        const rowCenterY = UIScene.ROW_Y + UIScene.SEG_H / 2;

        this.hud = this.add.container(0, 0);

        // Level - top left.
        const levelLabel = this.add.text(16, rowCenterY, 'LEVEL', {
            fontFamily: 'Arial', fontSize: '13px', color: '#8fa8c4'
        }).setOrigin(0, 0.5).setShadow(1, 1, '#000000', 3);
        this.levelText = this.add.text(74, rowCenterY, '1', {
            fontFamily: 'Arial Black, Arial', fontSize: '22px', color: '#ffffff'
        }).setOrigin(0, 0.5).setShadow(1, 1, '#000000', 4);

        // Shields - top right.
        const shieldLabel = this.add.text(this.barLeft() - 12, rowCenterY, 'SHIELDS', {
            fontFamily: 'Arial', fontSize: '13px', color: '#8fa8c4'
        }).setOrigin(1, 0.5).setShadow(1, 1, '#000000', 3);

        this.shieldBar = this.add.graphics();

        this.hud.add([levelLabel, this.levelText, shieldLabel, this.shieldBar]);

        // Start hidden: the HUD only belongs in-game. This scene is active from
        // boot (so it can own the M key everywhere), but the level/shields must
        // not flash over the loading screen or the menus - the Level shows it,
        // the splash/credits keep it hidden.
        this.hud.setVisible(false);

        this.drawShields(UIScene.MAX_SHIELDS, false);

        // M toggles the background music. This scene is always active, so the
        // key works on every screen (splash, game, credits).
        this.input.keyboard.on('keydown-M', () => {
            // 'mute'/'setMute' live on the concrete (WebAudio/HTML5) sound, not
            // the BaseSound type, so access them loosely.
            const music = this.registry.get('music') as any;
            if (music) {
                music.setMute(!music.mute);
            }
        });
    }

    updateUI(level: number, shields: number) {
        if (!this.levelText) {
            return;
        }
        this.levelText.setText(`${level}`);
        this.drawShields(shields, shields <= 0);
        this.updateDanger(shields);
    }

    // Left edge of the shield bar (it grows leftwards from BAR_RIGHT).
    private barLeft(): number {
        const totalW = UIScene.MAX_SHIELDS * (UIScene.SEG_W + UIScene.SEG_GAP) - UIScene.SEG_GAP;
        return UIScene.BAR_RIGHT - totalW;
    }

    // Draw the segmented shield bar, colour shifting from teal to amber to red
    // as the shields run down. While in danger (zero shields and still alive)
    // the whole bar turns red as a warning; after game over it shows plain empty.
    private drawShields(shields: number, danger: boolean) {
        const segments = UIScene.MAX_SHIELDS;
        const filled = Math.max(0, Math.min(segments, shields));
        const empty = filled === 0;
        const bx = this.barLeft();
        const by = UIScene.ROW_Y;

        let color = 0x52d6b0; // teal (healthy)
        if (filled <= 2) {
            color = 0xff5a5a; // red (critical)
        } else if (filled <= 5) {
            color = 0xffc24a; // amber (low)
        }

        this.shieldBar.clear();
        for (let i = 0; i < segments; i++) {
            const sx = bx + i * (UIScene.SEG_W + UIScene.SEG_GAP);
            if (i < filled) {
                this.shieldBar.fillStyle(color, 1);
            } else if (empty && danger) {
                // Out of shields, still alive: red warning - the next hit ends the run.
                this.shieldBar.fillStyle(0xff3030, 0.9);
            } else {
                this.shieldBar.fillStyle(0x24344c, 0.85);
            }
            this.shieldBar.fillRoundedRect(sx, by, UIScene.SEG_W, UIScene.SEG_H, 2);
        }
    }

    // Called when the run ends: stop the flashing warning and leave a plain
    // empty bar (no red, no pulse) on the game-over screen.
    onGameOver() {
        if (this.dangerTween) {
            this.dangerTween.stop();
            this.dangerTween = null;
        }
        if (this.shieldBar) {
            this.shieldBar.setAlpha(1);
            this.drawShields(0, false);
        }
    }

    // Flash the (red) shield bar while shields are at zero; stop once recovered.
    private updateDanger(shields: number) {
        if (shields <= 0) {
            if (!this.dangerTween) {
                this.shieldBar.setAlpha(1);
                this.dangerTween = this.tweens.add({
                    targets: this.shieldBar, alpha: 0.2, duration: 300, yoyo: true, repeat: -1
                });
            }
        } else if (this.dangerTween) {
            this.dangerTween.stop();
            this.dangerTween = null;
            this.shieldBar.setAlpha(1);
        }
    }

    // Show/hide the HUD - hidden on the splash/credits screens, shown in-game.
    setHudVisible(visible: boolean) {
        if (this.hud) {
            this.hud.setVisible(visible);
        }
    }
}
